import React, { useState } from 'react';
import {
  View,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../hooks/useTheme';

interface SearchBarProps {
  value: string;
  onChangeText: (text: string) => void;
  placeholder?: string;
  style?: Record<string, unknown>;
  containerStyle?: Record<string, unknown>;
  showClearButton?: boolean;
  onClear?: () => void;
  autoFocus?: boolean;
  editable?: boolean;
  /** 'compact' reduces height and padding; 'wide' for top-of-page, wider and taller */
  size?: 'default' | 'compact' | 'wide';
}

const SearchBar: React.FC<SearchBarProps> = ({
  value,
  onChangeText,
  placeholder = 'Search...',
  style,
  containerStyle,
  showClearButton = true,
  onClear,
  autoFocus = false,
  editable = true,
  size = 'default',
}) => {
  const { colors } = useTheme();
  const [isFocused, setIsFocused] = useState(false);

  const handleClear = () => {
    if (onClear) {
      onClear();
    } else {
      onChangeText('');
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  return (
    <View style={[styles.container, containerStyle]}>
      <View
        style={[
          styles.searchBar,
          {
            backgroundColor: 'transparent',
            borderColor: isFocused ? colors.primary : colors.border,
          },
          isFocused
            ? [
                styles.searchBarFocused,
                Platform.OS === 'web'
                  ? {
                      // @ts-ignore: boxShadow is web-only
                      boxShadow: `0px 4px 12px 0px ${colors.primary}1A`,
                    }
                  : { shadowColor: colors.primary },
              ]
            : Platform.OS === 'web'
              ? {
                  // @ts-ignore: boxShadow is web-only
                  boxShadow: `0px 4px 12px 0px ${colors.cardText}1A`,
                }
              : { shadowColor: colors.cardText },
          style,
          size === 'compact' && styles.searchBarCompact,
          size === 'wide' && styles.searchBarWide,
        ]}
      >
        <Ionicons
          name="search"
          size={
            size === 'compact'
              ? Platform.OS === 'web'
                ? 14
                : 16
              : size === 'wide'
                ? Platform.OS === 'web'
                  ? 18
                  : 20
                : Platform.OS === 'web'
                  ? 16
                  : 18
          }
          color={isFocused ? colors.primary : colors.secondary}
          style={[
            styles.searchIcon,
            size === 'compact' && styles.searchIconCompact,
            size === 'wide' && styles.searchIconWide,
          ]}
        />
        <TextInput
          style={[
            styles.searchInput,
            { color: colors.text },
            size === 'compact' && styles.searchInputCompact,
            size === 'wide' && styles.searchInputWide,
          ]}
          placeholder={placeholder}
          placeholderTextColor={colors.secondary}
          value={value}
          onChangeText={onChangeText}
          onFocus={handleFocus}
          onBlur={handleBlur}
          autoFocus={autoFocus}
          editable={editable}
          underlineColorAndroid="transparent"
        />
        {showClearButton && value.length > 0 && (
          <TouchableOpacity onPress={handleClear} style={styles.clearButton}>
            <Ionicons
              name="close-circle"
              size={Platform.OS === 'web' ? 16 : 18}
              color={colors.secondary}
            />
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: Platform.OS === 'web' ? 0 : 16,
  },
  searchBar: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 8,
    paddingHorizontal: 10,
    height: Platform.OS === 'web' ? 32 : 36,
    borderWidth: 1,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
    transition: 'all 0.2s ease',
  },
  searchBarCompact: {
    paddingHorizontal: 8,
    height: Platform.OS === 'web' ? 28 : 32,
    borderRadius: 6,
  },
  searchBarWide: {
    width: '100%',
    paddingHorizontal: 14,
    height: Platform.OS === 'web' ? 46 : 48,
    borderRadius: 12,
  },
  searchIconWide: {
    marginRight: 10,
  },
  searchInputWide: {
    fontSize: Platform.OS === 'web' ? 15 : 16,
  },
  searchBarFocused: {
    // Enhanced focus state - both icon and text field are highlighted
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 4,
    borderWidth: 2,
  },
  searchIcon: {
    marginRight: 6,
  },
  searchIconCompact: {
    marginRight: 4,
  },
  searchInput: {
    flex: 1,
    minWidth: 0,
    height: '100%',
    fontSize: Platform.OS === 'web' ? 13 : 14,
    borderWidth: 0,
    outlineStyle: 'none',
  },
  searchInputCompact: {
    fontSize: Platform.OS === 'web' ? 12 : 13,
  },
  clearButton: {
    padding: 4,
    paddingLeft: 4,
    paddingRight: 4,
    marginLeft: 4,
  },
});

export default SearchBar;
